import { useState } from 'react';
import {
  falError, setJobInCatalog, type Job3D, type KeptPieces, type PieceItem,
} from './threeDApi';
import { invalidatePartModels } from './partModels';
import { useLanguage } from '../../contexts/LanguageContext';

// KEEPING A MESH IN THE DATABASE, or taking it out — for a part's runs and for the whole
// product's, through this one hook.
//
// ONE PATH, BECAUSE THE PRODUCT HAD NONE. The switch was written into the part's panel and
// handed to the strip and the viewer as an optional prop, and the product's panel simply never
// passed it: an assembled chair had no way into the database for no reason anybody decided. The
// strip and the stage REQUIRE one of these now (see MeshRunStrip, MeshStage), so a panel that
// shows meshes cannot leave the switch out, and this is the only thing that makes one.
//
// What changes in the caller's list is the caller's: a part patches its rows in place, a product
// re-reads its runs. Several runs of one part or product may be kept.
export type CatalogSwitch = {
  // The run whose switch is in flight.
  busyId: number | null;
  onToggle: (job: Job3D, on: boolean) => void;
  // WHAT THE LAST PRESS DID WITH THE COMPONENTS, for the panel that offers to keep a whole
  // product — null for a part's switch, and for a press that has not landed.
  pieces: KeptPieces | null;
  // WHY THE SWITCH CANNOT BE PRESSED HERE, where it cannot: a component part's model is the
  // piece of the kept product and follows it (see set_in_catalog). Drawn, locked, with this
  // as its title — the state is still worth seeing. A function where it depends on the run: a
  // piece of hardware's own runs have a switch, the pieces cut out of a set it was built in
  // follow the set.
  fixed?: string | ((job: Job3D) => string | undefined);
};

// Why this run's switch is locked, if it is.
export const fixedFor = (catalog: CatalogSwitch, job: Job3D): string | undefined =>
  (typeof catalog.fixed === 'function' ? catalog.fixed(job) : catalog.fixed);

export function useCatalogSwitch(
  onSaved: (saved: Job3D) => void,
  onError: (message: string) => void,
  // THE PARTS THIS PRODUCT IS MADE OF, read at the moment of the press: the name each
  // component was modelled under, and the catalog row the page matched it to. Given, the
  // components are kept with the product (see _keep_pieces); a part's own switch has none
  // to give and passes nothing.
  //
  // A FUNCTION rather than a list, for the same reason the run's own inputs are: what is
  // kept must be what the parts table said when the button went down, and a list captured a
  // render earlier is a list from before the categories landed.
  parts?: () => PieceItem[],
): CatalogSwitch {
  const { t } = useLanguage();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pieces, setPieces] = useState<KeptPieces | null>(null);
  const onToggle = (job: Job3D, on: boolean) => {
    setBusyId(job.id);
    onError('');
    setPieces(null);
    void setJobInCatalog(job.id, on, on ? parts?.() : undefined)
      .then(({ job: saved, pieces: cut }) => {
        onSaved(saved);
        setPieces(cut);
        // The part selector's dots and the phase bar are about the database too, and live
        // outside every panel. The components land there as well, which is the whole point
        // of keeping them: this is what puts a dot on every part of a chair at once.
        invalidatePartModels();
      })
      .catch((e) => onError(falError(e, t('notice.3d.dbFailed'))))
      .finally(() => setBusyId(null));
  };
  return { busyId, onToggle, pieces };
}
